import { gbp, shortDate } from "@/lib/format";

const GROUPS = [
  { key: "right", label: "Right documents", detail: "Correct type, covering the requested months",
    names: ["Right type of document", "From the right month", "Covers the right months", "Statement dates shown", "Pay date shown", "Recent tax year"] },
  { key: "original", label: "Originals", detail: "Not screenshots, edited or duplicated",
    names: ["Original document, not a screenshot", "No signs of editing", "Not a duplicate"] },
  { key: "names", label: "Names match", detail: "The same name on every document",
    names: ["Name shown on document", "Name matches other documents"] },
  { key: "numbers", label: "Payslip numbers add up", detail: "Gross pay minus deductions equals net pay",
    names: ["Gross and net pay shown", "Numbers add up"] },
  { key: "id", label: "ID in date", detail: "Passport or driving licence not expired",
    names: ["Expiry date visible", "ID is in date"] },
];
const KNOWN = new Set(GROUPS.flatMap((g) => g.names));

const judge = (checks, base) => {
  if (!checks.length) return { ...base, value: "Not checked yet", status: "pending" };
  const failed = checks.filter((c) => !c.passed);
  return failed.length
    ? { ...base, detail: failed.map((c) => c.name).join(" · "), status: "failed" }
    : { ...base, status: "passed" };
};

export function documentRows(checks = [], complete) {
  const rows = GROUPS.map((g) => judge(checks.filter((c) => g.names.includes(c.name)), { key: g.key, label: g.label, detail: g.detail }));
  const other = checks.filter((c) => !KNOWN.has(c.name));
  if (other.length) rows.push(judge(other, { key: "other", label: "Other checks", detail: "Cross-checks between your documents and bank data" }));
  return complete ? rows.filter((r) => r.status !== "pending") : rows;
}

export function incomeRows(a, l) {
  const m = a.income_multiplier ?? l.criteria.income_multiplier;
  const required = a.required_income ?? l.monthly_rent * m;
  const rows = [];
  if (a.annual_income == null) {
    rows.push({ key: "annual", label: "Annual income", detail: "Worked out once every document is accepted", value: "—", status: "pending" });
  } else {
    const p = a.income_parts || {};
    let detail = p.monthly_gross ? `${gbp(p.monthly_gross)} a month gross × 12` : "";
    if (p.sharer != null) detail = `Applicant ${gbp(p.applicant)} + rent sharer ${gbp(p.sharer)}`;
    else if (p.sa302 != null && p.sa302 >= (p.applicant || 0)) detail = "From the SA302 tax calculation";
    rows.push({ key: "annual", label: "Annual income", detail, value: gbp(a.annual_income), status: a.meets_income ? "passed" : "failed" });
  }
  rows.push({ key: "required", label: "Required", detail: `${gbp(l.monthly_rent)} rent × ${m}`, value: gbp(required) });
  if (a.guarantor) {
    const g = a.guarantor;
    rows.push({ key: "guarantor", label: "Guarantor income", value: gbp(g.annual_income), status: g.meets ? "passed" : "failed",
      detail: `Needs ${gbp(l.monthly_rent)} × ${l.criteria.guarantor_income_multiplier} = ${gbp(g.required_income)}` });
  }
  return rows;
}

export function payRows(a, l) {
  const matches = a.bank_matches || [];
  if (!matches.length) return [{ key: "net", label: "Net pay in the bank", value: "—", status: "pending" }];
  const tol = l.criteria.bank_net_tolerance_pct;
  return matches.map((r, i) => {
    const says = `Payslip says ${gbp(r.net, 2)}`;
    const base = { key: `pay-${i}`, label: `Pay due ${shortDate(r.pay_date)}` };
    if (r.status === "matched") return { ...base, value: gbp(r.credit.amount, 2), detail: `${says} · arrived ${shortDate(r.credit.date)}`, status: "passed" };
    if (r.status === "gap") return { ...base, value: gbp(r.credit.amount, 2), detail: `${says} · ${r.gap_pct}% off, ${tol}% allowed`, status: "failed" };
    return { ...base, value: "Not found", detail: `${says} · nothing arrived around that date`, status: "failed" };
  });
}

export const metSummary = (rows) => {
  const judged = rows.filter((r) => r.status === "passed" || r.status === "failed");
  return judged.length ? `${judged.filter((r) => r.status === "passed").length} of ${judged.length} met` : null;
};
